import { useState } from 'react';
import { BlurFade } from '@/components/ui/blur-fade';
import { PulsatingButton } from '@/components/ui/pulsating-button';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { getApiErrorMessage } from '@/lib/apiErrors';

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const result = await apiService.submitContactForm({
      name: formData.name,
      email: formData.email,
      organization: formData.organization || undefined,
      message: formData.message,
    });

    if (result.success) {
      setStatus('success');
    } else {
      setStatus('error');
      setErrorMessage(getApiErrorMessage(result, 'Something went wrong. Please try again.'));
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="mx-auto max-w-5xl">
        <BlurFade inView>
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
              Ready to Transform Your Support Operations?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              EU ServiceLine is tailored to your organization's needs.
              Contact us to learn how we can streamline your customer support.
            </p>
          </div>
        </BlurFade>

        <BlurFade delay={0.15} inView>
          <div className="max-w-lg mx-auto">
            {status === 'success' ? (
              <div className="rounded-xl border border-border bg-card p-8 shadow-sm text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">Message Sent!</h3>
                <p className="text-muted-foreground mb-6">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setStatus('idle');
                    setFormData({ name: '', email: '', organization: '', message: '' });
                  }}
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-4"
              >
                {status === 'error' && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                    {errorMessage}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      disabled={status === 'loading'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={status === 'loading'}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organization">Organization</Label>
                  <Input
                    id="organization"
                    placeholder="Your organization name"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    disabled={status === 'loading'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your support needs..."
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    disabled={status === 'loading'}
                  />
                </div>
                <PulsatingButton
                  type="submit"
                  pulseColor="hsl(217, 91%, 35%)"
                  className="w-full py-3 text-base"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 inline animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Get in Touch
                      <ArrowRight className="ml-2 h-4 w-4 inline" />
                    </>
                  )}
                </PulsatingButton>
              </form>
            )}
          </div>
        </BlurFade>
      </div>
    </section>
  );
}
